/**
 * passageCopyGuard — blocks lifting passage text out of a tutoring session.
 *
 * Students must still be able to SELECT the passage (selection is how the highlighter reads a
 * range, and it is how a student tracks the line they are on), so this deliberately does NOT
 * touch `user-select`. It cancels the exits instead: clipboard copy/cut, and drag-out.
 *
 * WHY DOCUMENT-LEVEL, NOT A HANDLER ON THE PASSAGE ELEMENT: the browser fires `copy` at the
 * element holding the START of the selection, not at the passage. Verified in Chromium — with
 * handlers bound to the passage container, select-all (Ctrl+A) then Ctrl+C fires `copy` on the
 * page's first element, sails past the container, and copies the passage verbatim. Selecting
 * from any chrome above the passage and dragging down into it does the same. So the guard
 * listens on the document and cancels whenever the selection INTERSECTS a registered passage,
 * whatever the event target is. Copying elsewhere (a student's own answer) is unaffected.
 *
 * This is a deterrent, not enforcement — the passage is in the page source and in the network
 * response, so devtools still reaches it. It stops casual copy-paste into an AI chat.
 */

import { useEffect, useRef } from 'react';
import type { MouseEvent } from 'react';

/** Passage elements currently on screen. PassagePanel and PassageCard are both mounted at
 *  every viewport (they hide each other with CSS), so expect two entries during a session. */
const guardedNodes = new Set<HTMLElement>();
let listening = false;

function selectionTouchesPassage(): boolean {
  const selection = window.getSelection();
  if (!selection || selection.isCollapsed || selection.rangeCount === 0) return false;

  for (let i = 0; i < selection.rangeCount; i += 1) {
    const range = selection.getRangeAt(i);
    for (const node of guardedNodes) {
      // A hidden node (the panel CSS-hidden at this viewport) still intersects, which is
      // harmless: its twin holds the same text and the copy is blocked either way.
      if (range.intersectsNode(node)) return true;
    }
  }
  return false;
}

function handleGuardedEvent(event: Event) {
  if (selectionTouchesPassage()) {
    event.preventDefault();
  }
}

function startListening() {
  if (listening) return;
  // Capture phase: cancel before any other handler can act on the event.
  document.addEventListener('copy', handleGuardedEvent, true);
  document.addEventListener('cut', handleGuardedEvent, true);
  document.addEventListener('dragstart', handleGuardedEvent, true);
  listening = true;
}

function stopListening() {
  if (!listening) return;
  document.removeEventListener('copy', handleGuardedEvent, true);
  document.removeEventListener('cut', handleGuardedEvent, true);
  document.removeEventListener('dragstart', handleGuardedEvent, true);
  listening = false;
}

/**
 * Guards the passage element the returned ref is attached to.
 *
 * Spread `guardProps` onto that same element: it suppresses the right-click menu (where "Copy"
 * lives), which is positional and so stays element-scoped rather than document-scoped.
 *
 * @example
 * ```tsx
 * const { ref, guardProps } = usePassageCopyGuard<HTMLDivElement>();
 * return <div ref={ref} {...guardProps}>{passage}</div>;
 * ```
 */
export function usePassageCopyGuard<T extends HTMLElement>() {
  const ref = useRef<T>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    guardedNodes.add(node);
    startListening();

    return () => {
      guardedNodes.delete(node);
      if (guardedNodes.size === 0) stopListening();
    };
  }, []);

  return {
    ref,
    guardProps: {
      onContextMenu: (event: MouseEvent) => event.preventDefault(),
    },
  };
}

/** Test-only: clears cross-test listener state. */
export function __resetPassageCopyGuardForTests() {
  guardedNodes.clear();
  stopListening();
}
